import { useCallback, useEffect, useMemo, useState } from "react";
import Papa from "papaparse";
import { useNavigate, useParams } from "react-router-dom";
import { AdminRoutes } from "config/routes";
import { downloadFile } from "utils/downloads";
import { removeDuplicatedElements } from "utils/Array";
import { REQUIREMENT } from "helpers/constants";
import {
  initialValuesCompetency,
  initialValuesEPA,
  REQUIREMENT_COMPETENCY_TYPE,
} from "pages/Requirements/CreateAndEdit/constants";
import { OPTIONS as RUBRIC_TIME_OPTIONS } from "pages/Requirements/CreateAndEdit/components/TableEvaluation";
import {
  useAddEvaluationMetricMutation,
  useAddOrganizationRequirementMutation,
  useAddRequirementCompetenciesMutation,
  useAddRequirementMutation,
  useAddRequirementResourcesMutation,
  useAddRequirementTagsMutation,
  useAddTagMutation,
  useGetOrganizationByIdLazyQuery,
  useGetRequirementsLazyQuery,
  useGetSpeciesQuery,
  useGetTagsLazyQuery,
} from "graphql/__generated__/hooks";
import {
  buildTagIdByName,
  getOrCreateOrgExclusiveTagId,
} from "./orgExclusiveTag";
import { STEPS, STEP_LABELS } from "./config";
import {
  getRequirementTemplateContent,
  mapDescriptionToDbFields,
  parseRequirementsCsv,
  ParsedRubricEntry,
  resolveRequirementName,
  CSV_DELIMITER,
} from "./parseRequirementsCsv";
import {
  ImportMode,
  ImportReport,
  ProcessingSettings,
  RequirementImportType,
  ResolvedCompetencyLink,
} from "./types";

const DEFAULT_RUBRIC_SKILL_LEVEL_LABELS = [
  "Day-1 Ready Clinician",
  "Advanced Novice Clinician",
  "Novice Clinician",
  "Not Clinic Ready",
  "Not Applicable",
  "",
];

function defaultProcessing(
  requirementType: RequirementImportType,
): ProcessingSettings {
  const base =
    requirementType === REQUIREMENT.REQUIREMENT_EPA ||
    requirementType === REQUIREMENT.REQUIREMENT_ITER
      ? initialValuesEPA
      : initialValuesCompetency;

  return {
    hour: base.hour,
    minutes: base.minutes,
    animal_size:
      requirementType === REQUIREMENT.REQUIREMENT_COMPETENCY
        ? (base as typeof initialValuesCompetency).animal_size
        : "",
    species_id: null,
    min_performance: base.min_performance,
    min_score: base.min_score,
    min_small_animals_count: base.min_small_animals_count ?? 0,
    min_large_animals_count: base.min_large_animals_count ?? 0,
    requirement_type: REQUIREMENT_COMPETENCY_TYPE.EPA,
    tags: [],
    resources: [],
    rubric_skill_level_labels: [...DEFAULT_RUBRIC_SKILL_LEVEL_LABELS],
    rubric_time_labels: RUBRIC_TIME_OPTIONS.map((option) => option.label),
  };
}

function applyRubricLabels(
  rubrics: ParsedRubricEntry[],
  processing: ProcessingSettings,
): Array<ParsedRubricEntry & {
  skill_level_label: string | null;
  target_achievement_time_label: string | null;
}> {
  return rubrics.map((rubric) => ({
    ...rubric,
    skill_level_label:
      processing.rubric_skill_level_labels[rubric.skill_level - 1]?.trim() ||
      null,
    target_achievement_time_label:
      processing.rubric_time_labels[rubric.target_achievement_time - 1]?.trim() ||
      null,
  }));
}

type Props = {
  mode: ImportMode;
  requirementType: RequirementImportType;
};

export const useImportRequirements = ({ mode, requirementType }: Props) => {
  const { organizationId } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState(STEPS.UPLOAD);
  const [requirements, setRequirements] = useState<
    ReturnType<typeof parseRequirementsCsv>["requirements"]
  >([]);
  const [processing, setProcessing] = useState<ProcessingSettings>(() =>
    defaultProcessing(requirementType),
  );
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({
    current: 0,
    total: 0,
  });
  const [report, setReport] = useState<ImportReport>({
    success: [],
    error: [],
    renamed: [],
    missingLinks: [],
  });

  const [getOrganizationById, { data: organizationData }] =
    useGetOrganizationByIdLazyQuery();
  const [getRequirements, { data: requirementsData }] =
    useGetRequirementsLazyQuery();
  const [getTags, { data: tagsData }] = useGetTagsLazyQuery();
  const { data: speciesData } = useGetSpeciesQuery();

  const [addRequirement] = useAddRequirementMutation();
  const [addOrganizationRequirement] = useAddOrganizationRequirementMutation();
  const [addRequirementTags] = useAddRequirementTagsMutation();
  const [addRequirementCompetencies] = useAddRequirementCompetenciesMutation();
  const [addRequirementResources] = useAddRequirementResourcesMutation();
  const [addEvaluationMetric] = useAddEvaluationMetricMutation();
  const [addTag] = useAddTagMutation();

  useEffect(() => {
    getRequirements();
    getTags();
    if (mode === "org" && organizationId) {
      getOrganizationById({ variables: { organizationId } });
    }
  }, [mode, organizationId]);

  const organizationName = useMemo(
    () => organizationData?.organization_by_pk?.name || "",
    [organizationData],
  );

  const backRoute = useMemo(() => {
    if (mode === "org" && organizationId) {
      if (requirementType === REQUIREMENT.REQUIREMENT_EPA) {
        return `/organization/${organizationId}/epas`;
      }
      if (requirementType === REQUIREMENT.REQUIREMENT_ITER) {
        return `/organization/${organizationId}/iters`;
      }
      return `/organization/${organizationId}/competencies`;
    }
    if (requirementType === REQUIREMENT.REQUIREMENT_EPA) {
      return AdminRoutes.EPAList;
    }
    if (requirementType === REQUIREMENT.REQUIREMENT_ITER) {
      return AdminRoutes.ITERsList;
    }
    return AdminRoutes.CompetenciesList;
  }, [mode, organizationId, requirementType]);

  const activeLabel = useMemo(() => STEP_LABELS.indexOf(step), [step]);

  const existingNames = useMemo(() => {
    const names = new Set<string>();
    (requirementsData?.requirement || []).forEach((r) => {
      if (r.name) names.add(r.name.trim().toLowerCase());
    });
    return names;
  }, [requirementsData]);

  const competencyIdByName = useMemo(() => {
    const map: Record<string, string> = {};
    (requirementsData?.requirement || []).forEach((r) => {
      if (
        r.type === REQUIREMENT.REQUIREMENT_COMPETENCY &&
        r.name &&
        r.requirement_id
      ) {
        map[r.name.trim().toLowerCase()] = r.requirement_id;
      }
    });
    return map;
  }, [requirementsData]);

  // Resolve an existing competency by name, prioritizing the org-exclusive
  // "Name [Org Name]" variant before falling back to the shared catalog one.
  const resolveCompetencyId = useCallback(
    (competencyName: string): string | null => {
      const base = competencyName.trim();
      if (!base) return null;

      if (mode === "org" && organizationName) {
        const exclusiveName = `${base} [${organizationName}]`.toLowerCase();
        if (competencyIdByName[exclusiveName]) {
          return competencyIdByName[exclusiveName];
        }
      }

      return competencyIdByName[base.toLowerCase()] ?? null;
    },
    [competencyIdByName, mode, organizationName],
  );

  const resolveCompetencyLinks = useCallback(
    (competencyNames: string[]): ResolvedCompetencyLink[] =>
      competencyNames.map((csvName) => {
        const trimmed = csvName.trim();
        const competencyId = resolveCompetencyId(trimmed);

        if (!competencyId) {
          return { csvName: trimmed, matchedName: null, found: false };
        }

        const matchedRequirement = requirementsData?.requirement?.find(
          (r) => r.requirement_id === competencyId,
        );

        return {
          csvName: trimmed,
          matchedName: matchedRequirement?.name ?? trimmed,
          found: true,
        };
      }),
    [requirementsData, resolveCompetencyId],
  );

  const tagIdByName = useMemo(
    () => buildTagIdByName(tagsData?.tag || []),
    [tagsData],
  );

  const speciesList = useMemo(
    () => speciesData?.species || [],
    [speciesData],
  );

  const totalImportItems = requirements.length;

  const resolveOrgExclusiveTagId = async (): Promise<string | null> => {
    if (!organizationName) return null;
    return getOrCreateOrgExclusiveTagId({
      organizationName,
      tagIdByName,
      addTag,
      refreshTags: getTags,
    });
  };

  const handleStep = useCallback((nextStep: string) => {
    setStep(nextStep);
  }, []);

  const handleRequirementsFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      if (!event.target?.result) return;

      Papa.parse(event.target.result as string, {
        header: true,
        delimiter: CSV_DELIMITER,
        skipEmptyLines: true,
        complete: (results) => {
          const parsed = parseRequirementsCsv(
            (results.data || []) as Record<string, string>[],
            requirementType,
          );
          if (parsed.errors.length > 0) {
            setUploadError(parsed.errors.join("\n"));
            return;
          }
          if (parsed.requirements.length === 0) {
            setUploadError("No requirement rows found in CSV");
            return;
          }
          setRequirements(
            removeDuplicatedElements(parsed.requirements, "row_key"),
          );
          setUploadError(null);
        },
      });
    };
    reader.readAsText(file);
  };

  const handleDownloadRequirementTemplate = () => {
    const label =
      requirementType === REQUIREMENT.REQUIREMENT_EPA
        ? "epas"
        : requirementType === REQUIREMENT.REQUIREMENT_ITER
          ? "iters"
          : "competencies";
    downloadFile(
      getRequirementTemplateContent(requirementType),
      `${label}_import_template.csv`,
      "text/csv;charset=utf-8;",
    );
  };

  const linkTagsAndResources = async (
    requirementId: string,
    exclusiveTagId?: string | null,
  ) => {
    const tagObjects: { requirement_id: string; tag_id: string }[] = [];

    if (exclusiveTagId) {
      tagObjects.push({ requirement_id: requirementId, tag_id: exclusiveTagId });
    }

    processing.tags.forEach((tag) => {
      if (tag.tag_id) {
        tagObjects.push({ requirement_id: requirementId, tag_id: tag.tag_id });
      }
    });

    if (tagObjects.length > 0) {
      await addRequirementTags({ variables: { objects: tagObjects } });
    }

    const resourceObjects = processing.resources.reduce(
      (acc, resource) => {
        if (!resource.resource_id) {
          return acc;
        }
        acc.push({
          requirement_id: requirementId,
          resource_id: resource.resource_id,
        });
        return acc;
      },
      [] as { requirement_id: string; resource_id: string }[],
    );

    if (resourceObjects.length > 0) {
      await addRequirementResources({ variables: { objects: resourceObjects } });
    }
  };

  const createRequirement = async ({
    name,
    description,
    type,
    requirementTypeValue,
    namesInUse,
    renamed,
  }: {
    name: string;
    description: string;
    type: RequirementImportType;
    requirementTypeValue?: string | null;
    namesInUse: Set<string>;
    renamed: string[];
  }) => {
    const finalName = resolveRequirementName(
      name,
      namesInUse,
      mode === "org" ? organizationName : undefined,
    );

    if (finalName !== name) {
      renamed.push(`${name} → ${finalName}`);
    }

    const mappedFields = mapDescriptionToDbFields(type, description);
    const taskTime = `${processing.hour}:${String(processing.minutes).padStart(2, "0")}`;

    const result = await addRequirement({
      variables: {
        name: finalName,
        description: mappedFields.description,
        overview: mappedFields.overview,
        task_time: taskTime,
        animal_size: processing.animal_size || null,
        type,
        requirement_type: requirementTypeValue || null,
        min_performance: processing.min_performance,
        min_score: processing.min_score,
        min_small_animals_count: processing.min_small_animals_count,
        min_large_animals_count: processing.min_large_animals_count,
        species_id: processing.species_id,
      },
    });

    const requirementId =
      result.data?.insert_requirement?.returning?.[0]?.requirement_id;

    if (requirementId) {
      namesInUse.add(finalName.toLowerCase());
    }

    return { requirementId, finalName };
  };

  const createRubrics = async (
    requirementId: string,
    rubrics: ParsedRubricEntry[],
  ) => {
    if (rubrics.length === 0) {
      return;
    }

    await addEvaluationMetric({
      variables: {
        objects: applyRubricLabels(rubrics, processing).map((rubric) => ({
          requirement_id: requirementId,
          skill_level: rubric.skill_level,
          target_achievement_time: rubric.target_achievement_time,
          description: rubric.description,
          skill_level_label: rubric.skill_level_label,
          target_achievement_time_label: rubric.target_achievement_time_label,
        })),
      },
    });
  };

  const onSubmit = async () => {
    if (mode === "org" && !organizationId) {
      setUploadError("Organization context is required");
      return;
    }

    setLoading(true);
    setLoadingProgress({ current: 0, total: totalImportItems });

    const success: string[] = [];
    const errorRows: string[] = [];
    const renamed: string[] = [];
    const missingLinks: string[] = [];
    const namesInUse = new Set(existingNames);

    let exclusiveTagId: string | null = null;
    if (mode === "org") {
      exclusiveTagId = await resolveOrgExclusiveTagId();
      if (!exclusiveTagId) {
        setUploadError("Could not resolve organization exclusive tag");
        setLoading(false);
        return;
      }
    }

    const bumpProgress = () =>
      setLoadingProgress((p) => ({ ...p, current: p.current + 1 }));

    const isLinkCompetenciesImport =
      requirementType === REQUIREMENT.REQUIREMENT_EPA ||
      requirementType === REQUIREMENT.REQUIREMENT_ITER;

    for (const row of requirements) {
      try {
        const { requirementId, finalName } = await createRequirement({
          name: row.name,
          description: row.description,
          type: requirementType,
          requirementTypeValue:
            requirementType === REQUIREMENT.REQUIREMENT_COMPETENCY
              ? processing.requirement_type || REQUIREMENT_COMPETENCY_TYPE.EPA
              : null,
          namesInUse,
          renamed,
        });

        if (!requirementId) {
          errorRows.push(`${row.name} - failed to create requirement`);
          bumpProgress();
          continue;
        }

        if (mode === "org" && organizationId) {
          await addOrganizationRequirement({
            variables: {
              org_id: organizationId,
              requirement_id: requirementId,
            },
          });
        }

        // Link existing competencies, prioritizing org-exclusive ones.
        if (isLinkCompetenciesImport && row.competencies.length > 0) {
          const competencyObjects = row.competencies.reduce(
            (acc, competencyName) => {
              const competencyId = resolveCompetencyId(competencyName);
              if (!competencyId) {
                missingLinks.push(
                  `${finalName}: competency "${competencyName}" not found`,
                );
                return acc;
              }
              acc.push({
                requirement_id: requirementId,
                competency_id: competencyId,
              });
              return acc;
            },
            [] as { requirement_id: string; competency_id: string }[],
          );

          if (competencyObjects.length > 0) {
            await addRequirementCompetencies({
              variables: { objects: competencyObjects },
            });
          }
        }

        await linkTagsAndResources(requirementId, exclusiveTagId);
        if (row.rubrics.length > 0) {
          await createRubrics(requirementId, row.rubrics);
        }
        success.push(finalName);
      } catch (err: any) {
        errorRows.push(`${row.name} - ${err?.message || "import failed"}`);
      }
      bumpProgress();
    }

    setReport({ success, error: errorRows, renamed, missingLinks });
    setLoading(false);
    setStep(STEPS.COMPLETE);
  };

  const handleFinish = () => navigate(backRoute);
  const handleCancel = () => navigate(backRoute);

  return {
    step,
    activeLabel,
    requirements,
    processing,
    uploadError,
    loading,
    loadingProgress,
    report,
    organizationName,
    speciesList,
    backRoute,
    handleStep,
    setProcessing,
    handleRequirementsFile,
    handleDownloadRequirementTemplate,
    onSubmit,
    handleFinish,
    handleCancel,
    resolveCompetencyLinks,
  };
};
